import { Fragment, memo, ReactElement, useRef, useState } from 'react';

import { useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';

import { FileAcceptText } from './file-accept-text';
import { FileInput } from './file-input';
import { FileInputContent } from './file-input-content';
import { useFileTranslations } from './locale';
import { DefaultFile, FileFieldBaseProps } from './types';
import { UploadedFile, UploadedFileProps } from './uploaded-file';

export type FileFieldProps = FileFieldBaseProps & {
  /**
   * Previously uploaded file
   */
  defaultValue?: DefaultFile | File;
  /**
   * Callback with the uploaded file.
   * If null, the `defaultFile` was removed and/or the input was cleared.
   */
  onChange(file: File | null): void;
  hideLabel?: boolean;
  variant?: UploadedFileProps['variant'];
  labelIcon?: ReactElement<IconProps>;
  allowReplace?: boolean;
  hideRemove?: boolean;
  onPreview?(): void;
  onClick?(): void;
  description?: string;
};

export const FileField = memo<FileFieldProps>(
  ({
    label,
    accept,
    error,
    placeholder: userPlaceholder,
    defaultValue,
    onChange,
    isDisabled,
    hideLabel = false,
    variant = 'default',
    labelIcon,
    allowReplace = false,
    hideRemove = false,
    onPreview,
    onClick,
    description,
  }) => {
    const { t } = useFileTranslations();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const id = useAriaId('file');
    const [defaultFile, setDefaultFile] = useState<DefaultFile | File | undefined>(defaultValue);
    const [file, setFile] = useState<File | null>(null);
    const placeholder = userPlaceholder ?? t('placeholder');

    let content: JSX.Element;

    if (file) {
      content = (
        <UploadedFile
          isDisabled={isDisabled}
          name={file.name}
          variant={variant}
          onClick={onClick}
          onPreview={onPreview}
          onReplace={allowReplace ? () => fileInputRef.current?.click() : undefined}
          onRemove={
            hideRemove
              ? undefined
              : () => {
                  setFile(null);
                  onChange(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }
          }
        />
      );
    } else if (defaultFile) {
      content = (
        <UploadedFile
          isDisabled={isDisabled}
          name={defaultFile.name}
          path={'src' in defaultFile ? defaultFile.src : undefined}
          variant={variant}
          onClick={onClick}
          onPreview={onPreview}
          onReplace={allowReplace ? () => fileInputRef.current?.click() : undefined}
          onRemove={
            hideRemove
              ? undefined
              : () => {
                  setDefaultFile(undefined);
                  onChange?.(null);
                }
          }
        />
      );
    } else {
      content = (
        <FileInputContent
          isDisabled={isDisabled}
          error={error}
          placeholder={placeholder}
          onClick={() => fileInputRef.current?.click()}
        />
      );
    }

    const FormLabelContainer = hideLabel ? VisuallyHidden : Fragment;

    return (
      <>
        <Stack space={1}>
          <FormLabelContainer>
            <FormLabel htmlFor={id} text={label} icon={labelIcon} />
          </FormLabelContainer>
          <FieldDescription text={description} />
          <FileInput
            isDisabled={isDisabled}
            ref={fileInputRef}
            id={id}
            accept={accept}
            onChange={(newFile) => {
              setFile(newFile);
              onChange(newFile);
            }}
          />

          {content}

          <FileAcceptText accept={accept} />
          <FormError text={error} />
        </Stack>
      </>
    );
  }
);
